import { Pipe, PipeTransform } from '@angular/core';

import { SalesOrder_PaymentType, SalesOrder_ReceiptMethod } from '@proto/api/orders/resources';
import { Strings } from '@helper/strings';
import { AddressHelper } from '@helper/address';
import { Payments } from '@helper/payments';
import { ProviderTypes } from '@helper/provider-type';
import { Recipient, StoreAddress_Type } from '@proto/api/common/common';
import { Customer_Provider } from '@proto/api/customers/resources';

@Pipe({ name: 'receiptName', standalone: true })
export class ReceiptNamePipe implements PipeTransform {
  transform(type: SalesOrder_ReceiptMethod, content?: string): string {
    switch (type) {
      case SalesOrder_ReceiptMethod.RECEIPT_METHOD_MEMBER_CARRIER: {
        return $localize`個人 - 電子發票`;
      }
      case SalesOrder_ReceiptMethod.RECEIPT_METHOD_MOBILE_BARCODE: {
        const translate = $localize`（條碼：${content}）`;
        return $localize`個人 - 手機條碼載具${content ? translate : ''}`;
      }
      case SalesOrder_ReceiptMethod.RECEIPT_METHOD_TAX_ID_NUMBER: {
        const translate = $localize`（統編：${content}）`;
        return $localize`公司 - 電子發票${content ? translate : ''}`;
      }
      case SalesOrder_ReceiptMethod.RECEIPT_METHOD_DONATION_CODE: {
        return $localize`捐贈發票${content ? `（${content}）` : ''}`;
      }
      case SalesOrder_ReceiptMethod.RECEIPT_METHOD_PAPER: {
        return $localize`個人 - 紙本發票`;
      }
    }
    return '';
  }
}

@Pipe({ name: 'receiverName', standalone: true })
export class ReceiverNamePipe implements PipeTransform {
  public transform(paymentType: SalesOrder_PaymentType, recipient?: Recipient): string {
    if (!recipient || paymentType === SalesOrder_PaymentType.PAYMENT_TYPE_UNSPECIFIED) {
      return '';
    }
    switch (paymentType) {
      case SalesOrder_PaymentType.PAYMENT_TYPE_SEVEN_ELEVEN:
      case SalesOrder_PaymentType.PAYMENT_TYPE_FAMILY_MART: {
        return recipient.storeAddress?.name ?? '';
      }
      case SalesOrder_PaymentType.PAYMENT_TYPE_CREDIT_CARD_CTBC:
      case SalesOrder_PaymentType.PAYMENT_TYPE_CREDIT_CARD:
      case SalesOrder_PaymentType.PAYMENT_TYPE_LINE_PAY:
      case SalesOrder_PaymentType.PAYMENT_TYPE_CASH_ON_DELIVERY:
      case SalesOrder_PaymentType.PAYMENT_TYPE_APPLE_PAY: {
        return recipient.address?.name ?? '';
      }
      case SalesOrder_PaymentType.PAYMENT_TYPE_OVERSEA:
      case SalesOrder_PaymentType.PAYMENT_TYPE_OVERSEA_CTBC: {
        return `${recipient.overseaAddress?.firstName ?? ''} ${recipient.overseaAddress?.lastName ?? ''}`;
      }
      default: {
        return '';
      }
    }
  }
}

@Pipe({ name: 'payType', standalone: true })
export class PayTypePipe implements PipeTransform {
  public transform(paymentType: SalesOrder_PaymentType): string {
    return Payments.payTypeDisplay(paymentType);
  }
}

@Pipe({ name: 'deliveryAddress', standalone: true })
export class DeliveryAddressPipe implements PipeTransform {
  public transform(paymentType: SalesOrder_PaymentType, recipient?: Recipient, mask?: boolean): string {
    if (!recipient || paymentType === SalesOrder_PaymentType.PAYMENT_TYPE_UNSPECIFIED) {
      return '';
    }
    switch (paymentType) {
      case SalesOrder_PaymentType.PAYMENT_TYPE_SEVEN_ELEVEN:
      case SalesOrder_PaymentType.PAYMENT_TYPE_FAMILY_MART: {
        return `${recipient?.storeAddress?.storeId ?? ''}-${recipient?.storeAddress?.storeName ?? ''}`;
      }
      case SalesOrder_PaymentType.PAYMENT_TYPE_CREDIT_CARD_CTBC:
      case SalesOrder_PaymentType.PAYMENT_TYPE_CREDIT_CARD:
      case SalesOrder_PaymentType.PAYMENT_TYPE_LINE_PAY:
      case SalesOrder_PaymentType.PAYMENT_TYPE_CASH_ON_DELIVERY:
      case SalesOrder_PaymentType.PAYMENT_TYPE_APPLE_PAY: {
        const address = recipient?.address;
        const st = address?.street ?? '';
        const maskLength = st.length / 2 + 1;
        const street = st && mask ? Strings.mask(st, maskLength, '*') : st;
        return `${address?.postcode ? `${address?.postcode} ` : ''}${address?.city ? `${address?.city} ` : ''}${
          address?.area ? `${address?.area} ` : ''
        }${street}`;
      }
      case SalesOrder_PaymentType.PAYMENT_TYPE_OVERSEA:
      case SalesOrder_PaymentType.PAYMENT_TYPE_OVERSEA_CTBC: {
        return `${recipient.overseaAddress?.street ?? ''} ${recipient.overseaAddress?.city ?? ''} ${
          recipient.overseaAddress?.province ?? ''
        } ${
          AddressHelper.needPostcode(recipient.overseaAddress?.country ?? '')
            ? `${recipient.overseaAddress?.postcode ?? ''} `
            : ''
        }${recipient.overseaAddress?.country ?? ''}`;
      }
      default: {
        return '';
      }
    }
  }
}

@Pipe({ name: 'prefixStoreName', standalone: true })
export class PrefixStoreNamePipe implements PipeTransform {
  public transform(storeName: string | undefined, type: StoreAddress_Type | undefined): string {
    switch (type) {
      case StoreAddress_Type.TYPE_SEVEN: {
        return $localize`7-11 ${storeName}門市`;
      }
      case StoreAddress_Type.TYPE_FAMILY: {
        return $localize`全家 ${storeName}`;
      }
      default: {
        return storeName ?? '';
      }
    }
  }
}

@Pipe({ name: 'loginType', standalone: true })
export class LoginTypePipe implements PipeTransform {
  public transform(type: Customer_Provider | undefined): string {
    return ProviderTypes.providerTypeDisplay(type);
  }
}

@Pipe({ name: 'cartIcon', standalone: true })
export class CartIconPipe implements PipeTransform {
  public transform(productId: string, productIdSet: Set<string | undefined>): string {
    if (productId && productIdSet.has(productId)) {
      return 'assets/icon_index_cart_red.svg';
    }
    return 'assets/icon_index_cart.svg';
  }
}
